import * as React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';

import { cn } from '@/utils/styles';

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      'fixed flex flex-col inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  'overflow-hidden flex flex-col fixed z-50 gap-4 transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out md:rounded-4xl md:m-4 md:h-auto',
  {
    variants: {
      modal: {
        true:
          'max-md:top-auto max-md:max-h-[var(--sheet-modal-max-h,60%)] max-md:rounded-t-xl w-full',
      },
      variant: {
        default: 'bg-sheet shadow-sheet-inner',
        glass:
          'bg-clip-padding backdrop-filter bg-opacity-10 bg-background-blur-modal backdrop-blur-md shadow-glass-inner',
      },
      side: {
        center:
          'inset-0 w-full data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        top: 'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
        bottom:
          'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
        left: 'inset-y-0 left-0 h-full md:w-3/4 border-r max-md:data-[state=closed]:slide-out-to-bottom max-md:data-[state=open]:slide-in-from-bottom md:data-[state=closed]:slide-out-to-left md:data-[state=open]:slide-in-from-left md:max-w-sm',
        right:
          'inset-y-0 right-0 h-full md:w-3/4 border-l max-md:data-[state=closed]:slide-out-to-bottom max-md:data-[state=open]:slide-in-from-bottom md:data-[state=closed]:slide-out-to-right md:data-[state=open]:slide-in-from-right md:max-w-sm',
      },
    },
    defaultVariants: {
      side: 'right',
      variant: 'default',
    },
  },
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  hideCloseButton?: boolean;
  /** When `modal` is true, max height of the bottom drawer on mobile (e.g. "60%", "20%", "200px"). Defaults to "60%". */
  modalMaxHeight?: string;
}

const SheetInlineContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(
  (
    {
      side,
      variant,
      children,
      modal,
      modalMaxHeight,
      className,
      hideCloseButton,
      ...props
    },
    ref,
  ) => (
    <SheetPrimitive.Content
      ref={ref}
      className={cn(sheetVariants({ side, modal, variant }), className)}
      style={
        modal && modalMaxHeight
          ? { ['--sheet-modal-max-h' as string]: modalMaxHeight }
          : undefined
      }
      {...props}
    >
      {!hideCloseButton && (
        <SheetPrimitive.Close className="text-primary absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
          <X className="h-4 w-4 md:h-6 md:w-6" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      )}

      <div className="h-full flex flex-col overscroll-contain overflow-auto">
        {children}
      </div>
    </SheetPrimitive.Content>
  ),
);

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ modal, modalMaxHeight, ...props }, ref) => (
  <SheetPortal>
    {modal && (
      <SheetOverlay className="backdrop-blur-md bg-black/40 md:backdrop-blur-none md:bg-transparent" />
    )}
    <SheetInlineContent ref={ref} modal={modal} modalMaxHeight={modalMaxHeight} {...props} />
  </SheetPortal>
));
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('p-6 flex flex-col space-y-2 text-left', className)}
    {...props}
  />
);
SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'p-6 flex bg-background flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className,
    )}
    {...props}
  />
);
SheetFooter.displayName = 'SheetFooter';

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold text-foreground', className)}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn('p-6 text-sm text-muted-foreground', className)}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetInlineContent,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
